import {AiFeedCategory, ContentBlockType, ICaseVariable, IFormBody, StyleToken} from './types'
import {newParagraphBlock} from './utils'

// AI Feed 케이스(=카테고리) 목록. 구독 여부에 따라 문구/변수 블러 처리가 달라지는 케이스는 별도 id로 분리
export const AI_FEED_CATEGORY_LABEL: Record<AiFeedCategory, string> = {
    daily_profit: '데일리 리포트 - 수익금',
    daily_profit_unsubscribed: '데일리 리포트 - 수익금 (미구독)',
    win_streak: '데일리 리포트 - 연속수익 N일 달성',
    win_streak_unsubscribed: '데일리 리포트 - 연속수익 N일 달성 (미구독)',
    league_rank: '리그 우승',
    league_rank_unsubscribed: '리그 우승 (미구독)',
    tradermap_rank: '트레이더맵 우승',
    tradermap_rank_unsubscribed: '트레이더맵 우승 (미구독)',
    new_entry: '신규종목 첫 편입',
    ratio_increase: '포트폴리오 비중 상승',
    ratio_decrease: '포트폴리오 비중 하락',
    latest_news: '최신 뉴스',
}

// 어드민에서 아직 문구를 직접 설정하지 않는 케이스 — 생성/수정 선택지와 목록에서 제외
const INACTIVE_AI_FEED_CATEGORIES: AiFeedCategory[] = ['latest_news']

export const AI_FEED_CATEGORY_OPTIONS: {id: AiFeedCategory; value: string}[] = Object.entries(AI_FEED_CATEGORY_LABEL)
    .filter(([id]) => !INACTIVE_AI_FEED_CATEGORIES.includes(id as AiFeedCategory))
    .map(([id, value]) => ({id: id as AiFeedCategory, value}))

// 리스트 화면에서 카테고리를 큰 틀로 묶어 테이블을 분리해서 보여주기 위한 그룹핑
export const AI_FEED_GROUPS: {key: string; label: string; categories: AiFeedCategory[]}[] = [
    {key: 'profit', label: '수익 리포트', categories: ['daily_profit', 'daily_profit_unsubscribed']},
    {key: 'win_streak', label: '연속 수익 달성', categories: ['win_streak', 'win_streak_unsubscribed']},
    {
        key: 'rank',
        label: '랭킹 달성',
        categories: ['league_rank', 'league_rank_unsubscribed', 'tradermap_rank', 'tradermap_rank_unsubscribed'],
    },
    {key: 'new_entry', label: '신규종목 첫 편입', categories: ['new_entry']},
    {key: 'ratio_increase', label: '포트폴리오 비중 상승', categories: ['ratio_increase']},
    {key: 'ratio_decrease', label: '포트폴리오 비중 하락', categories: ['ratio_decrease']},
]

// ── Editor Contract ──────────────────────────────────────────────────────────
// GET /v3/admin/feed_template/kinds 응답 형태. 에디터의 변수/검증은 모두 계약에서 파생한다(하드코딩 금지).
// 런타임에는 useQueryFeedTemplateKinds()가 반환하는 live 계약을 컴포넌트로 주입해 사용하고,
// 이 상수는 응답 전/실패 시 폴백일 뿐이다. 값은 현재 live 응답과 일치시켜 둔다.
// - kinds + common_vars 의 변수만 강제 검증 대상 (계약 밖 {변수}는 저장 시 즉시 거부).
// - styles / block_types 는 참고용(비강제).
export interface IEditorContract {
    kinds: Record<string, string[]>
    common_vars: string[]
    styles: string[]
    block_types: string[]
}

export const EDITOR_CONTRACT: IEditorContract = {
    kinds: {
        daily_profit: ['PROFIT_RATE', 'PROFIT_AMT', 'BALANCE', 'DATE'],
        new_entry: ['SYMBOL', 'WEIGHT'],
        ratio_increase: ['SYMBOL', 'PREV_WEIGHT', 'WEIGHT', 'RANK'],
        ratio_decrease: ['SYMBOL', 'PREV_WEIGHT', 'WEIGHT', 'RANK'],
        win_streak: ['STREAK_DAYS', 'PROFIT_RATE'],
        league_rank: ['LEAGUE_NAME', 'PLACE', 'PERCENTILE', 'RATE', 'LEAGUE_ID'],
        tradermap_rank: ['LEAGUE_NAME', 'PLACE', 'PERCENTILE', 'RATE', 'TRADERMAP_ID'],
    },
    common_vars: ['TRADER_NAME', 'TRADER_ID'],
    styles: ['display', 'title', 'headline', 'body', 'caption', 'bold', 'positive', 'negative', 'accent', 'blur', 'link', 'block'],
    block_types: ['paragraph', 'container', 'chart', 'tag', 'icon', 'image', 'button', 'button_mo'],
}

// 어드민 카테고리 → 계약 kind 매핑. (미)구독 변형은 같은 kind를 공유하고, latest_news는 계약 대상이 아니다(undefined).
export const CATEGORY_TO_KIND: Record<AiFeedCategory, string | undefined> = {
    daily_profit: 'daily_profit',
    daily_profit_unsubscribed: 'daily_profit',
    win_streak: 'win_streak',
    win_streak_unsubscribed: 'win_streak',
    league_rank: 'league_rank',
    league_rank_unsubscribed: 'league_rank',
    tradermap_rank: 'tradermap_rank',
    tradermap_rank_unsubscribed: 'tradermap_rank',
    new_entry: 'new_entry',
    ratio_increase: 'ratio_increase',
    ratio_decrease: 'ratio_decrease',
    latest_news: undefined,
}

// 계약은 변수명만 반환하므로 표시용 설명은 어드민에서 관리한다 (없으면 변수명 그대로 노출).
export const VARIABLE_DESCRIPTIONS: Record<string, string> = {
    TRADER_NAME: '트레이더 고유 닉네임 (한/영)',
    TRADER_ID: '트레이더 고유 ID',
    PROFIT_RATE: '데일리 리포트 당일 기준 수익률',
    PROFIT_AMT: '데일리 리포트 당일 기준 수익금(통화 변수)',
    BALANCE: '당일 기준 평가 잔고(통화 변수)',
    DATE: '기준 날짜',
    SYMBOL: '암호화폐 자산 심볼',
    WEIGHT: '포트폴리오 내 비중',
    PREV_WEIGHT: '변경 전(기존) 비중',
    RANK: '포트폴리오 내 비중 순위',
    STREAK_DAYS: '연속 수익 달성 일수',
    ATH_RATE: '신고점(최고) 수익률',
    LEAGUE_NAME: '리그/대회명 (한/영)',
    LEAGUE_ID: '리그의 고유 ID',
    TRADERMAP_ID: '트레이더맵 리그의 고유 ID',
    PLACE: '최종 순위',
    PERCENTILE: '상위 백분위(%)',
    RATE: '대회 기준 수익률',
}

const toCaseVariable = (name: string): ICaseVariable => ({name, description: VARIABLE_DESCRIPTIONS[name] ?? name})

// 카테고리에서 사용 가능한(=강제 검증 통과) 변수명 목록 = 공통 변수(common_vars) + 해당 kind 변수.
// contract는 live /kinds 응답을 주입받고, 미지정 시 폴백 상수(EDITOR_CONTRACT)를 쓴다.
export const allowedVariableNames = (
    category?: AiFeedCategory,
    contract: IEditorContract = EDITOR_CONTRACT,
): string[] => {
    const kind = category ? CATEGORY_TO_KIND[category] : undefined
    const kindVars = kind ? contract.kinds[kind] ?? [] : []
    return [...contract.common_vars, ...kindVars]
}

// 특정 카테고리의 변수 셋(설명 포함). 변수 칩/기본 목록 렌더용. 전부 계약에서 파생.
export const caseVariablesFor = (
    category?: AiFeedCategory,
    contract: IEditorContract = EDITOR_CONTRACT,
): ICaseVariable[] => allowedVariableNames(category, contract).map(toCaseVariable)

// 계약 전체 변수(공통 + 모든 kind)를 중복 없이 한 줄 라인업으로 — 변수 목록 모달/전체 삽입용.
export const allCaseVariables = (contract: IEditorContract = EDITOR_CONTRACT): ICaseVariable[] =>
    [...contract.common_vars, ...Object.values(contract.kinds).flat()].reduce<ICaseVariable[]>((acc, name) => {
        if (!acc.some(item => item.name === name)) acc.push(toCaseVariable(name))
        return acc
    }, [])

// "타입 목록 보기" 모달의 type별 설명(어드민 소유). 목록 자체는 live 계약(block_types)에서 파생하고
// 여기서는 각 type의 설명만 관리한다. 계약에 없는 type은 목록에 나오지 않는다.
export const BLOCK_TYPE_DESCRIPTIONS: Record<string, string> = {
    paragraph:
        '텍스트 조각. text(문구, {TOKEN} 변수 포함 가능), style(서식 토큰 자유 조합: display·title·headline·body·caption·bold·positive·negative·blur·link·block, 예: title_bold_block). 연속된 조각은 기본 이어붙고, style에 block이 있는 조각만 그 앞에서 줄바꿈됩니다.',
    container:
        '범용 레이아웃. variant(card/tile), background(neutral/skyblue/positive/orange/negative), direction(row/column), align, justify, gap, flex(grow — 부모 안에서 남은 공간 채움), url(전체 탭), blocks(중첩 — 아무 타입이나 재귀).',
    chart: '막대/도넛 차트. variant(bar/donut), text(도넛 제목), segments(label·percent·style: positive/negative/accent). segments가 비면 스킵됩니다.',
    tag: '작은 pill 라벨. text(라벨), variant(primary/secondary/tertiary/negative). text가 없으면 스킵됩니다.',
    icon: '이미지 아이콘. icon_url(이미지 URL — 이모지는 paragraph로), size(sm/md/lg). icon_url이 없으면 스킵됩니다.',
    image: '이미지. image(이미지 URL — 업로드하면 자동 입력). 없으면 스킵됩니다.',
    button: '웹 링크 버튼. text(라벨), url(이동 링크). text/url 둘 다 있어야 그려집니다.',
    button_mo: '앱(딥링크) 링크 버튼. text(라벨), url(이동 링크). 같은 blocks 안에 button과 함께 있으면 button_mo는 스킵됩니다.',
}

// live 계약의 block_types 순서대로 {type, 설명} 정의표를 만든다. 설명이 없으면 빈 문자열.
export const blockTypeDefinitions = (blockTypes: string[]): {type: string; description: string}[] =>
    blockTypes.map(type => ({type, description: BLOCK_TYPE_DESCRIPTIONS[type] ?? ''}))

// ContentBlock v2 type
export const BLOCK_TYPE_LABEL: Record<ContentBlockType, string> = {
    paragraph: '문단(텍스트/변수)',
    container: '컨테이너(레이아웃)',
    chart: '차트(막대/도넛)',
    tag: '태그(라벨)',
    icon: '아이콘(이미지)',
    image: '이미지',
    button: '링크 버튼(웹)',
    button_mo: '링크 버튼(앱/딥링크)',
}

// 모든 v2 타입은 직접 추가할 수 있다(container 내부 재귀 포함). 별도 자동 관리 타입은 없다.
const INACTIVE_BLOCK_TYPES: ContentBlockType[] = []

export const BLOCK_TYPE_OPTIONS: {id: ContentBlockType; value: string}[] = Object.entries(BLOCK_TYPE_LABEL)
    .filter(([id]) => !INACTIVE_BLOCK_TYPES.includes(id as ContentBlockType))
    .map(([id, value]) => ({id: id as ContentBlockType, value}))

// chart 구간 색상처럼 select로 하나만 고르는 곳에서 쓰는 색 토큰 목록.
// (문단 서식은 RichParagraphRun 툴바에서 조합 버튼으로 직접 만든다.)
export const STYLE_TOKEN_LABEL: Record<string, string> = {
    positive: '초록 (positive)',
    negative: '빨강 (negative)',
    accent: '강조 (accent)',
}

export const STYLE_TOKEN_OPTIONS: {id: StyleToken; value: string}[] = Object.entries(STYLE_TOKEN_LABEL).map(
    ([id, value]) => ({id, value}),
)

// "타입 목록 보기" 모달의 스타일 미리보기 목록. token은 style에 넣는 값, label/description은 표시용.
// 실제 미리보기 class는 styleTokenToClassName(token)에서 파생하므로 여기 목록과 렌더가 항상 일치한다.
export const STYLE_GUIDE_ITEMS: {token: string; label: string; description: string}[] = [
    {token: 'display', label: 'Display', description: '가장 큰 크기 (32px)'},
    {token: 'title', label: 'Title', description: '24px'},
    {token: 'headline', label: 'Headline', description: '18px'},
    {token: 'body', label: 'Body', description: '본문 기본 크기 (14px)'},
    {token: 'caption', label: 'Caption', description: '12px'},
    {token: 'bold', label: 'Bold', description: '볼드 (font-bold)'},
    {token: 'positive', label: 'positive', description: '초록색 텍스트'},
    {token: 'negative', label: 'negative', description: '빨강색 텍스트'},
    {token: 'blur', label: 'blur', description: '마스킹 대상 (viewer.is_masked 시 ●로 치환)'},
    {token: 'link', label: 'link', description: '밑줄'},
    {token: 'block', label: 'block', description: '이 조각 앞에서 줄바꿈'},
]

export const emptyFormBody = (): IFormBody => ({
    id: '',
    post_type: 'auto_generated',
    category: 'daily_profit',
    content_blocks: [newParagraphBlock()],
})

// 관리자 요청 채널 = localStorage의 lang. api.ts의 language 헤더와 동일 규칙(en만 'en', 그 외 'ko').
// 채널마다 서로 다른 DB를 바라본다 (생성/조회/삭제 모두 현재 채널 기준).
export type FeedChannel = 'ko' | 'en'

export const getFeedChannel = (): FeedChannel => (localStorage?.getItem('lang') === 'en' ? 'en' : 'ko')

